import { useState, useEffect, useCallback } from 'react';
import { hrms } from '../../services/api';
import Card from '../../components/common/Card';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import { theme } from '../../theme';

const sel = {
  padding: '0.5rem 0.875rem',
  border: `1.5px solid ${theme.border}`,
  borderRadius: '0.5rem',
  fontSize: '0.875rem',
  color: theme.text,
  outline: 'none',
  background: '#fff',
};

function ClientVisitManagement() {
  const [requests, setRequests]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [saving, setSaving]           = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      const r = await hrms.getClientVisitRequests(params);
      setRequests(Array.isArray(r.data) ? r.data : []);
    } catch { setRequests([]); }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleApprove = async (id) => {
    try { await hrms.approveClientVisitRequest(id); fetch(); }
    catch (err) { alert(err?.response?.data?.detail || 'Failed to approve'); }
  };

  const handleReject = async () => {
    setSaving(true);
    try {
      await hrms.rejectClientVisitRequest(rejectModal, rejectReason);
      setRejectModal(null); setRejectReason(''); fetch();
    } catch (err) { alert(err?.response?.data?.detail || 'Failed to reject'); }
    finally { setSaving(false); }
  };

  const lbl = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.text, marginBottom: '0.35rem' };
  const inp = { width: '100%', padding: '0.6rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', boxSizing: 'border-box' };

  const statusColor = { pending: '#F59E0B', approved: '#10B981', rejected: '#EF4444', cancelled: '#9CA3AF' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Client Visit Requests</h1>
          <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
            View and manage all employee client visit requests
          </p>
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={sel}>
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Table */}
      <Card padding="0">
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: '#F8FAFC' }}>
                {['Employee', 'Emp Code', 'Visit Date', 'Client', 'Location', 'Purpose', 'Duration', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, color: theme.textMuted, borderBottom: `2px solid ${theme.border}`, fontSize: '0.78rem', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: '3rem', color: theme.textMuted }}>Loading…</td></tr>
              ) : requests.length === 0 ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: '3rem', color: theme.textMuted }}>No client visit requests found</td></tr>
              ) : requests.map(r => (
                <tr key={r.id} style={{ borderBottom: `1px solid ${theme.border}` }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '0.75rem 1rem', fontWeight: 500 }}>{r.employee_name || '—'}</td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', background: '#F3F4F6', padding: '0.15rem 0.4rem', borderRadius: 4 }}>
                      {r.emp_code || '—'}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap' }}>
                    {r.visit_date ? new Date(r.visit_date).toLocaleDateString('en-IN') : '—'}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontWeight: 500 }}>{r.client_name || '—'}</td>
                  <td style={{ padding: '0.75rem 1rem', color: theme.textMuted, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.client_location || '—'}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', color: theme.textMuted, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.purpose || '—'}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', color: theme.textMuted, whiteSpace: 'nowrap' }}>{r.expected_duration || '—'}</td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <Badge status={r.status} byName={r.approved_by_name || r.pending_approver_name} />
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    {r.status === 'pending' && (
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <Button size="sm" variant="success" onClick={() => handleApprove(r.id)}>Approve</Button>
                        <Button size="sm" variant="danger" onClick={() => { setRejectModal(r.id); setRejectReason(''); }}>Reject</Button>
                      </div>
                    )}
                    {r.status !== 'pending' && (
                      <span style={{ fontSize: '0.78rem', color: statusColor[r.status] || theme.textMuted, fontWeight: 500, textTransform: 'capitalize' }}>
                        {r.status}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Reject Modal */}
      <Modal
        isOpen={!!rejectModal}
        onClose={() => setRejectModal(null)}
        title="Reject Client Visit Request"
        footer={
          <>
            <Button variant="outlineGray" onClick={() => setRejectModal(null)}>Cancel</Button>
            <Button variant="danger" loading={saving} onClick={handleReject}>Reject</Button>
          </>
        }
      >
        <label style={lbl}>Reason for Rejection *</label>
        <textarea
          style={{ ...inp, resize: 'vertical', minHeight: '80px' }}
          value={rejectReason}
          onChange={e => setRejectReason(e.target.value)}
          placeholder="Enter reason for rejection..."
        />
      </Modal>
    </div>
  );
}

export default ClientVisitManagement;
